import { createFormDataRequest } from "@/helpers/FormHelpers";
import { IPaginateResponse } from "@/interfaces/Fetch";
import Meeting, {
  CompanyMeetingFilters,
  MeetingFormSend,
} from "@/interfaces/Meeting";
import api from "../api";

/**
 * Ce qu'invalide la création d'un rendez-vous d'entreprise. Le calendrier
 * montage en fait partie depuis que le bouton « Nouveau » y crée des montages
 * d'entreprise : sans ce tag, il attendait un socket que plusieurs instances
 * d'API peuvent perdre (spec 2026-10-08).
 */
export const storeCompanyMeetingInvalidations = (companyId: string) => [
  { type: "CommercialBooking" as const, id: "LIST" },
  { type: "CompanyMeetings" as const, companyId, id: "LIST" },
  { type: "Companies" as const, id: companyId },
  { type: "Companies" as const, id: "LIST" },
  { type: "CompanyUsers" as const, companyId, id: "LIST" },
  { type: "Meetings" as const, id: "LIST" },
  { type: "MontageMeetings" as const, id: "LIST" },
  { type: "Users" as const, id: "CALENDAR_FILTERABLE" },
];

export const companyMeetings = api.injectEndpoints({
  endpoints: (build) => ({
    getCompanyMeetings: build.query<
      IPaginateResponse<Meeting>,
      [string, CompanyMeetingFilters]
    >({
      query: ([id, data]) => ({
        url: `/companies/${id}/meetings`,
        params: createFormDataRequest(data),
      }),
      providesTags: (result, _error, [companyId]) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "CompanyMeetings" as const,
                companyId,
                id,
              })),
              { type: "CompanyMeetings", companyId, id: "LIST" },
            ]
          : [{ type: "CompanyMeetings", companyId, id: "LIST" }],
    }),
    storeCompanyMeetings: build.mutation<Meeting, [string, MeetingFormSend]>({
      query: ([id, body]) => ({
        url: `/companies/${id}/meetings`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, [id]) => storeCompanyMeetingInvalidations(id),
    }),
    updateCompanyMeeting: build.mutation<
      Meeting,
      [string, string, Partial<MeetingFormSend>]
    >({
      query: ([companyId, id, body]) => ({
        url: `/companies/${companyId}/meetings/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_, _error, [companyId, id]) => [
        { type: "CommercialBooking", id: "LIST" },
        { type: "CompanyMeetings", companyId, id },
        { type: "Companies", id: companyId },
        { type: "Meetings", id: "LIST" },
        // Harmless no-op for a commercial meeting (no matching cache entry);
        // required for a montage meeting so the montage calendar refetches.
        { type: "MontageMeetings", id },
        { type: "MontageMeetings", id: "LIST" },
      ],
    }),
    getCompanyMeeting: build.query<Meeting, [string, string]>({
      query: ([companyId, id]) => ({
        url: `/companies/${companyId}/meetings/${id}`,
      }),
      providesTags: (result, _error, [companyId, id]) => [
        { type: "CompanyMeetings", companyId, id },
      ],
    }),
    deleteCompanyMeeting: build.mutation<Meeting, [string, string]>({
      query: ([companyId, id]) => ({
        url: `/companies/${companyId}/meetings/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, _error, [companyId, id]) => [
        { type: "CommercialBooking", id: "LIST" },
        { type: "CompanyMeetings", companyId, id: "LIST" },
        { type: "MontageMeetings", id },
        { type: "MontageMeetings", id: "LIST" },
      ],
    }),
  }),

  overrideExisting: true,
});

export const {
  useStoreCompanyMeetingsMutation,
  useGetCompanyMeetingQuery,
  useGetCompanyMeetingsQuery,
  useUpdateCompanyMeetingMutation,
  useLazyGetCompanyMeetingQuery,
  useDeleteCompanyMeetingMutation,
} = companyMeetings;
